import React, { useState, useEffect } from "react";
import ArrowLeft from "frontend/Components/DesignSystem/Arrows/ArrowLeft";
import ArrowRight from "frontend/Components/DesignSystem/Arrows/ArrowRight";
import "../../../css/Adviser/Pages/VideoCarouselPopup.css";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";

/**
 * VideoCarouselPopup is a modal popup component that displays a horizontal carousel of YouTube videos.
 * Users can navigate through the videos using left/right arrows or by clicking on navigation dots.
 * The carousel is responsive and adjusts the card width on window resize.
 *
 * @example
 * <VideoCarouselPopup isOpen={isPopupOpen} onClose={handleClose} />
 */
const VideoCarouselPopup = ({ isOpen, onClose }) => {
  const [cardWidth, setCardWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const videos = [
    { url: "https://www.youtube.com/embed/qvNb1dtB71g" },
    { url: "https://www.youtube.com/embed/pkIMaJrOIGE" },
    { url: "https://www.youtube.com/embed/YHb4BZ__w68" },
    { url: "https://www.youtube.com/embed/y280_4geqD4" },
    { url: "https://www.youtube.com/embed/Xl3i86O_74I" },
    { url: "https://www.youtube.com/embed/E0CnxKA9Uas" },
    { url: "https://www.youtube.com/embed/SmZdfJ_X1qk" },
    { url: "https://www.youtube.com/embed/MxQDDTQIbeM" },
    // { url: "https://www.youtube.com/embed/X5iujnZaWQc" },
    { url: "https://www.youtube.com/embed/e00--a_88OI" },
  ];
  useEffect(() => {
    const updateCardWidth = () => {
      if (cardRef.current) {
        setCardWidth(cardRef.current.offsetWidth);
      }
    };

    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);
    return () => window.removeEventListener("resize", updateCardWidth);
  }, [videos]);

  /**
   * Handles the action of moving to the previous item in the video carousel.
   * If the current index is greater than 0, it scrolls the container to the previous card
   * and updates the current index state accordingly.
   *
   * @function
   * @returns {void}
   */
  const handlePrev = () => {
    if (currentIndex > 0) {
      const newScrollPosition = (currentIndex - 1) * cardWidth;
      containerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
      setCurrentIndex((prev) => prev - 1);
    }
  };

  /**
   * Handles the action of moving to the next video in the carousel.
   * If the current index is less than the last video index, it scrolls the container
   * to the next video card smoothly and updates the current index state.
   *
   * @function
   * @returns {void}
   */
  const handleNext = () => {
    if (currentIndex < videos.length - 1) {
      const newScrollPosition = (currentIndex + 1) * cardWidth;
      containerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
      setCurrentIndex((prev) => prev + 1);
    }
  };

  /**
   * Handles the click event on a carousel navigation dot.
   * Scrolls the carousel container to the corresponding card and updates the current index.
   *
   * @param {number} index - The index of the carousel card to scroll to.
   */
  const handleDotClick = (index) => {
    if (containerRef.current) {
      const newScrollPosition = index * cardWidth;
      containerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="close-button" onClick={onClose}>
          <CloseIcon />
        </button>
        <div className="carousel-container">
          <ArrowLeft onClick={handlePrev} disabled={currentIndex === 0} />
          <div className="carousel-track" ref={containerRef}>
            {videos.map((video, index) => (
              <div
                key={index}
                ref={index === 0 ? cardRef : null}
                className="carousel-card"
              >
                <iframe
                  width="100%"
                  height="515"
                  src={video.url}
                  title={`YouTube Video ${index}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ))}
          </div>
          <ArrowRight
            onClick={handleNext}
            disabled={currentIndex === videos.length - 1}
          />
        </div>
        <div className="dots-container">
          {videos.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => handleDotClick(index)}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoCarouselPopup;
